import { useState, useEffect } from 'react';
import { ideasApi, companiesApi, trendsApi, pitchApi } from '../lib/api';

const SCRIPT_TYPES = [
  { value: 'elevator_60', label: '60-Second Elevator Pitch', icon: '\uD83C\uDFA4', desc: 'Quick verbal pitch for chance encounters' },
  { value: 'meeting_10min', label: '10-Minute Meeting Script', icon: '\uD83D\uDCCB', desc: 'Structured script with Q&A prep' },
  { value: 'email_pitch', label: 'Email Pitch Template', icon: '\u2709\uFE0F', desc: 'Company-specific email with subject line' },
  { value: 'sizzle_shotlist', label: 'Sizzle Video Shot List', icon: '\uD83C\uDFAC', desc: 'Shot-by-shot video production guide' },
  { value: 'tiktok_style', label: 'TikTok-Style Pitch', icon: '\uD83D\uDCF1', desc: '30-60 second social media pitch' },
  { value: 'leave_behind', label: 'One-Page Leave-Behind', icon: '\uD83D\uDCC4', desc: 'Professional summary document' },
];

const TYPE_LABELS = Object.fromEntries(SCRIPT_TYPES.map(t => [t.value, t.label]));

export default function PitchBuilder({ initialContext, onContextConsumed }) {
  const [ideas, setIdeas] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trendMatches, setTrendMatches] = useState([]);
  const [savedScripts, setSavedScripts] = useState([]);

  const [selectedIdeaId, setSelectedIdeaId] = useState(initialContext?.ideaId || null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialContext?.companyId || '');
  const [selectedType, setSelectedType] = useState('');
  const [selectedTrendId, setSelectedTrendId] = useState(initialContext?.trendId || '');

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Viewing/editing a script
  const [viewingScript, setViewingScript] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialContext) {
      if (initialContext.ideaId) setSelectedIdeaId(initialContext.ideaId);
      if (initialContext.companyId) setSelectedCompanyId(initialContext.companyId);
      if (initialContext.trendId) setSelectedTrendId(initialContext.trendId);
      onContextConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContext]);

  useEffect(() => {
    if (selectedIdeaId) {
      loadScriptsForIdea();
      loadTrendMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdeaId]);

  const loadData = async () => {
    try {
      const [ideasData, companiesData] = await Promise.all([
        ideasApi.list(),
        companiesApi.list(),
      ]);
      setIdeas((ideasData.ideas || []).filter(i => i.status !== 'archived'));
      setCompanies(companiesData.companies || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const loadScriptsForIdea = async () => {
    try {
      const data = await pitchApi.list({ idea_id: selectedIdeaId });
      setSavedScripts(data.scripts || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadTrendMatches = async () => {
    try {
      const data = await trendsApi.getMatches({ idea_id: selectedIdeaId });
      setTrendMatches((data.matches || []).filter(m => m.status !== 'dismissed'));
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerate = async () => {
    if (!selectedIdeaId || !selectedType) {
      setError('Select an idea and script type');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const data = await pitchApi.generate({
        idea_id: selectedIdeaId,
        company_id: selectedCompanyId || undefined,
        script_type: selectedType,
        trend_id: selectedTrendId || undefined,
      });
      setViewingScript(data.script);
      setEditContent(data.script.content);
      await loadScriptsForIdea();
    } catch (err) {
      setError(err.message);
    }
    setGenerating(false);
  };

  const handleSaveEdit = async () => {
    if (!viewingScript) return;
    try {
      const data = await pitchApi.update(viewingScript.id, editContent);
      setViewingScript(data.script);
      setEditing(false);
      await loadScriptsForIdea();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await pitchApi.delete(id);
      if (viewingScript?.id === id) setViewingScript(null);
      await loadScriptsForIdea();
    } catch (err) {
      console.error(err);
    }
  };

  // Script viewer
  if (viewingScript) {
    return (
      <div>
        <button onClick={() => { setViewingScript(null); setEditing(false); }}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-vault-navy mb-4 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Pitch Builder
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-xl font-bold text-vault-navy">
                    {TYPE_LABELS[viewingScript.script_type] || viewingScript.script_type}
                  </h1>
                  <div className="flex items-center gap-2 mt-1">
                    {viewingScript.metadata?.company_name && (
                      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                        {viewingScript.metadata.company_name}
                      </span>
                    )}
                    {viewingScript.metadata?.trend_topic && (
                      <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full">
                        {viewingScript.metadata.trend_topic}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {editing ? (
                    <>
                      <button onClick={handleSaveEdit}
                        className="px-3 py-1.5 bg-vault-gold hover:bg-vault-gold-dark text-white text-sm font-medium rounded-lg transition-colors">
                        Save
                      </button>
                      <button onClick={() => { setEditing(false); setEditContent(viewingScript.content); }}
                        className="px-3 py-1.5 border border-vault-border text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditing(true)}
                        className="px-3 py-1.5 bg-vault-gold hover:bg-vault-gold-dark text-white text-sm font-medium rounded-lg transition-colors">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(viewingScript.id)}
                        className="px-3 py-1.5 border border-vault-error/30 text-vault-error text-sm font-medium rounded-lg hover:bg-red-50 transition-colors">
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              {editing ? (
                <textarea
                  value={editContent}
                  onChange={e => setEditContent(e.target.value)}
                  rows={25}
                  className="w-full px-4 py-3 border border-vault-border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold resize-none"
                />
              ) : (
                <div className="prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {viewingScript.content}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar metadata */}
          <div>
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <h3 className="text-sm font-semibold text-gray-500 mb-3">Details</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-xs text-gray-400">Type</div>
                  <div className="text-vault-navy font-medium">{TYPE_LABELS[viewingScript.script_type]}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Created</div>
                  <div className="text-vault-navy">{new Date(viewingScript.created_at).toLocaleString()}</div>
                </div>
                {viewingScript.trend_angle && (
                  <div>
                    <div className="text-xs text-gray-400">Trend Angle</div>
                    <div className="text-vault-navy">{viewingScript.trend_angle}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="text-center py-16 text-gray-400">Loading...</div>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-vault-navy">Pitch Builder</h1>
        <p className="text-gray-500 text-sm mt-1">Generate pitch scripts tailored to specific companies and trends</p>
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-vault-error text-sm">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Configuration */}
        <div className="lg:col-span-2 space-y-4">
          {/* Idea selection */}
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <h2 className="text-lg font-semibold text-vault-navy mb-3">1. Select Idea</h2>
            <select value={selectedIdeaId || ''} onChange={e => setSelectedIdeaId(e.target.value || null)}
              className="w-full px-3 py-2 border border-vault-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-vault-gold">
              <option value="">Choose an idea...</option>
              {ideas.map(idea => <option key={idea.id} value={idea.id}>{idea.title}</option>)}
            </select>
          </div>

          {/* Company selection */}
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <h2 className="text-lg font-semibold text-vault-navy mb-1">2. Target Company</h2>
            <p className="text-xs text-gray-400 mb-3">Optional. The pitch will be customized to their wishlist.</p>
            <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
              className="w-full px-3 py-2 border border-vault-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-vault-gold">
              <option value="">No specific company (generic pitch)</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Trend selection */}
          {trendMatches.length > 0 && (
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <h2 className="text-lg font-semibold text-vault-navy mb-1">3. Trend Angle</h2>
              <p className="text-xs text-gray-400 mb-3">Optional. Weave a trending topic into your pitch.</p>
              <select value={selectedTrendId} onChange={e => setSelectedTrendId(e.target.value)}
                className="w-full px-3 py-2 border border-vault-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-vault-gold">
                <option value="">No trend angle</option>
                {trendMatches.map(m => (
                  <option key={m.trend_id} value={m.trend_id}>
                    {m.trend_alerts?.trend_topic} ({Math.round((m.match_score || 0) * 100)}% match)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Script type selection */}
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <h2 className="text-lg font-semibold text-vault-navy mb-3">
              {trendMatches.length > 0 ? '4' : '3'}. Script Type
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SCRIPT_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    selectedType === type.value
                      ? 'border-vault-gold bg-yellow-50 shadow-sm'
                      : 'border-vault-border hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span>{type.icon}</span>
                    <span className="font-medium text-vault-navy text-sm">{type.label}</span>
                  </div>
                  <p className="text-xs text-gray-500">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={generating || !selectedIdeaId || !selectedType}
            className="w-full py-3 bg-vault-gold hover:bg-vault-gold-dark text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-lg"
          >
            {generating ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating Pitch...
              </>
            ) : (
              'Generate Pitch Script'
            )}
          </button>
        </div>

        {/* Right: Script library */}
        <div>
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <h2 className="text-lg font-semibold text-vault-navy mb-4">Script Library</h2>
            {!selectedIdeaId ? (
              <p className="text-sm text-gray-400 text-center py-4">Select an idea to see saved scripts</p>
            ) : savedScripts.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No scripts yet for this idea</p>
            ) : (
              <div className="space-y-2">
                {savedScripts.map(script => (
                  <div key={script.id} className="p-3 bg-vault-surface rounded-lg group cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => { setViewingScript(script); setEditContent(script.content); }}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-vault-navy">
                          {TYPE_LABELS[script.script_type] || script.script_type}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {script.metadata?.company_name && (
                            <span className="text-xs text-gray-400">{script.metadata.company_name}</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {new Date(script.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(script.id); }}
                        className="text-gray-300 hover:text-vault-error opacity-0 group-hover:opacity-100 transition-opacity">
                        &times;
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
